import { Link } from "react-router-dom";
import { articles } from "@/mocks/articles";

export default function ContentSections() {
  return (
    <>
      {/* Intro Section */}
      <IntroSection />

      {/* Comparison Table */}
      <ComparisonTable />

      {/* Why Choose Us */}
      <WhyChooseUs />

      {/* Blog Preview */}
      <BlogPreview />

      {/* Safety Section */}
      <SafetySection />

      {/* Final CTA */}
      <FinalCTA />
    </>
  );
}

function IntroSection() {
  return (
    <section className="py-14 md:py-20 px-4 md:px-6 bg-background-50">
      <div className="max-w-4xl mx-auto">
        <h2 className="font-heading text-2xl md:text-4xl font-bold text-foreground-950 mb-6 text-center">
          Why Itaobuy Sheet Matters
        </h2>
        <div className="space-y-4 text-foreground-700 leading-relaxed text-sm md:text-base">
          <p>
            <strong className="text-foreground-950">Itaobuy sheet</strong> is a modern structured
            shopping intelligence system that helps users compare, organize, and access curated
            purchasing data efficiently. Unlike traditional browsing, an{" "}
            <strong className="text-foreground-950">itaobuy spreadsheet</strong> centralizes product
            information, pricing, and availability into a single, easy-to-navigate interface. This
            approach saves hours of manual comparison and eliminates the guesswork from online
            shopping.
          </p>
          <p>
            With the right <strong className="text-foreground-950">itaobuy sheet</strong> setup, you
            can instantly filter through thousands of listings, compare seller ratings, verify product
            authenticity, and track price history — all within a streamlined data-driven framework.
            Whether you are sourcing fashion, accessories, or lifestyle goods, a properly configured
            itaobuy spreadsheet gives you a decisive advantage in making informed purchasing
            decisions.
          </p>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mt-8">
            {[
              { icon: "ri-time-line", title: "Compare Multiple Systems Instantly", desc: "Side-by-side itaobuy sheet comparisons in seconds, not hours." },
              { icon: "ri-database-2-line", title: "Save Time with Structured Data", desc: "Organized spreadsheet views eliminate manual sorting and filtering." },
              { icon: "ri-shield-check-line", title: "Avoid Unreliable Sources", desc: "Only verified and vetted itaobuy spreadsheet listings are included." },
              { icon: "ri-lightbulb-line", title: "Find Optimized Purchasing Decisions", desc: "Data-driven recommendations based on real performance metrics." },
            ].map((item, i) => (
              <div key={i} className="flex gap-3 p-4 bg-background-100 rounded-xl">
                <span className="w-10 h-10 flex-shrink-0 flex items-center justify-center rounded-full bg-primary-100 text-primary-600">
                  <i className={`${item.icon} text-lg`}></i>
                </span>
                <div>
                  <h3 className="font-semibold text-sm md:text-base text-foreground-900 mb-1">{item.title}</h3>
                  <p className="text-xs md:text-sm text-foreground-600">{item.desc}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
}

function ComparisonTable() {
  const platforms = [
    { name: "Itaobuy Sheet A", speed: "Fast", accuracy: "High", trust: "9.5 / 10", bestFor: "Beginners", highlight: true },
    { name: "Itaobuy Sheet B", speed: "Medium", accuracy: "High", trust: "9.0 / 10", bestFor: "Advanced Users", highlight: false },
    { name: "Itaobuy Sheet C", speed: "Fast", accuracy: "Medium", trust: "8.7 / 10", bestFor: "Bulk Buyers", highlight: false },
    { name: "Itaobuy Sheet D", speed: "Very Fast", accuracy: "Very High", trust: "9.8 / 10", bestFor: "Professional Sourcing", highlight: false },
  ];

  return (
    <section className="py-14 md:py-20 px-4 md:px-6 bg-background-100">
      <div className="max-w-5xl mx-auto">
        <h2 className="font-heading text-2xl md:text-4xl font-bold text-foreground-950 mb-3 text-center">
          Top Itaobuy Spreadsheet Rankings
        </h2>
        <p className="text-sm md:text-base text-foreground-600 text-center mb-8 max-w-xl mx-auto">
          Independent comparison of leading itaobuy sheet platforms based on speed, accuracy, and trustworthiness.
        </p>

        {/* Desktop table */}
        <div className="hidden md:block overflow-x-auto">
          <table className="w-full border-collapse">
            <thead>
              <tr className="border-b-2 border-background-300">
                <th className="text-left py-3 px-4 text-xs font-semibold text-foreground-500 uppercase tracking-wider">Platform</th>
                <th className="text-left py-3 px-4 text-xs font-semibold text-foreground-500 uppercase tracking-wider">Speed</th>
                <th className="text-left py-3 px-4 text-xs font-semibold text-foreground-500 uppercase tracking-wider">Accuracy</th>
                <th className="text-left py-3 px-4 text-xs font-semibold text-foreground-500 uppercase tracking-wider">Trust Score</th>
                <th className="text-left py-3 px-4 text-xs font-semibold text-foreground-500 uppercase tracking-wider">Best For</th>
              </tr>
            </thead>
            <tbody>
              {platforms.map((p, i) => (
                <tr key={i} className={`border-b border-background-200/70 ${p.highlight ? "bg-primary-50/60" : ""}`}>
                  <td className="py-3.5 px-4 text-sm font-semibold text-foreground-900">{p.name}</td>
                  <td className="py-3.5 px-4 text-sm text-foreground-700">{p.speed}</td>
                  <td className="py-3.5 px-4 text-sm text-foreground-700">{p.accuracy}</td>
                  <td className="py-3.5 px-4">
                    <span className={`inline-flex items-center gap-1 text-sm font-semibold ${i === 0 ? "text-accent-600" : "text-foreground-700"}`}>
                      {p.trust}
                    </span>
                  </td>
                  <td className="py-3.5 px-4">
                    <span className="inline-flex px-2.5 py-1 text-xs font-medium rounded-full bg-secondary-100 text-secondary-800">
                      {p.bestFor}
                    </span>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        {/* Mobile cards */}
        <div className="md:hidden space-y-3">
          {platforms.map((p, i) => (
            <div key={i} className={`p-4 rounded-xl border ${p.highlight ? "bg-primary-50/60 border-primary-200" : "bg-background-50 border-background-200/70"}`}>
              <div className="flex items-center justify-between mb-2">
                <span className="font-semibold text-sm text-foreground-900">{p.name}</span>
                <span className="text-xs font-semibold text-accent-600">{p.trust}</span>
              </div>
              <div className="flex flex-wrap gap-2 text-xs text-foreground-600">
                <span className="px-2 py-0.5 rounded bg-background-100">Speed: {p.speed}</span>
                <span className="px-2 py-0.5 rounded bg-background-100">Accuracy: {p.accuracy}</span>
                <span className="px-2 py-0.5 rounded-full bg-secondary-100 text-secondary-800">{p.bestFor}</span>
              </div>
            </div>
          ))}
        </div>

        <div className="text-center mt-8">
          <a
            href="https://kaspreadsheet.com"
            target="_blank"
            rel="noopener nofollow"
            className="inline-flex px-6 py-3 bg-primary-500 text-background-50 font-semibold text-sm rounded-full hover:bg-primary-600 transition-colors whitespace-nowrap cursor-pointer"
          >
            Compare Full Rankings
          </a>
        </div>
      </div>
    </section>
  );
}

function WhyChooseUs() {
  const features = [
    {
      icon: "ri-bar-chart-2-line",
      title: "Real-Time Comparisons",
      desc: "Live itaobuy sheet data comparisons updated throughout the day, so you always see the latest rankings and availability.",
    },
    {
      icon: "ri-file-list-3-line",
      title: "Verified Spreadsheet Lists",
      desc: "Every itaobuy spreadsheet entry is manually verified by our team to ensure accuracy and eliminate duplicate or fraudulent listings.",
    },
    {
      icon: "ri-book-open-line",
      title: "Beginner to Advanced Guides",
      desc: "Comprehensive itaobuy sheet tutorials covering everything from basic navigation to advanced spreadsheet optimization techniques.",
    },
    {
      icon: "ri-shield-star-line",
      title: "Safety & Trust Verification",
      desc: "Built-in verification system that flags unreliable sources and highlights trusted itaobuy spreadsheet providers with proven track records.",
    },
    {
      icon: "ri-line-chart-line",
      title: "Data-Driven Ranking Model",
      desc: "Our proprietary algorithm ranks itaobuy sheet options based on multiple performance metrics, not just surface-level ratings.",
    },
    {
      icon: "ri-global-line",
      title: "Global Access & Support",
      desc: "Access your itaobuy spreadsheet data from anywhere, with support for multiple languages and international shipping optimization.",
    },
  ];

  return (
    <section className="py-14 md:py-20 px-4 md:px-6 bg-background-50">
      <div className="max-w-6xl mx-auto">
        <h2 className="font-heading text-2xl md:text-4xl font-bold text-foreground-950 mb-3 text-center">
          What You Get with Itaobuy Sheet
        </h2>
        <p className="text-sm md:text-base text-foreground-600 text-center mb-10 max-w-xl mx-auto">
          Our itaobuy spreadsheet platform combines powerful comparison tools with verified data for a seamless shopping intelligence experience.
        </p>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 md:gap-5">
          {features.map((f, i) => (
            <div key={i} className="p-5 md:p-6 bg-background-100 rounded-xl border border-background-200/70 hover:border-primary-200 transition-colors group">
              <span className="w-11 h-11 flex items-center justify-center rounded-lg bg-accent-100 text-accent-600 mb-4 group-hover:bg-accent-200 transition-colors">
                <i className={`${f.icon} text-xl`}></i>
              </span>
              <h3 className="font-semibold text-sm md:text-base text-foreground-900 mb-2">{f.title}</h3>
              <p className="text-xs md:text-sm text-foreground-600 leading-relaxed">{f.desc}</p>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

function BlogPreview() {
  return (
    <section className="py-14 md:py-20 px-4 md:px-6 bg-background-100">
      <div className="max-w-6xl mx-auto">
        <h2 className="font-heading text-2xl md:text-4xl font-bold text-foreground-950 mb-3 text-center">
          Latest Itaobuy Sheet Insights
        </h2>
        <p className="text-sm md:text-base text-foreground-600 text-center mb-10 max-w-xl mx-auto">
          In-depth articles and guides to help you master the itaobuy spreadsheet ecosystem. All content regularly updated with fresh insights.
        </p>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 md:gap-5">
          {articles.map((article) => (
            <Link
              key={article.slug}
              to={`/${article.slug}`}
              className="group block bg-background-50 rounded-xl border border-background-200/70 overflow-hidden hover:border-primary-300 transition-all duration-200 cursor-pointer"
            >
              <div className="relative h-44 overflow-hidden">
                <img
                  src={article.imageUrl}
                  alt={article.title}
                  className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-300"
                  loading="lazy"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-black/30 to-transparent"></div>
                <div className="absolute bottom-3 left-3">
                  <span className={`inline-block px-2.5 py-1 text-xs font-semibold rounded-full ${article.tagColor}`}>
                    {article.tag}
                  </span>
                </div>
              </div>
              <div className="p-4 md:p-5">
                <h3 className="font-heading text-sm md:text-base font-semibold text-foreground-900 mb-2 group-hover:text-primary-700 transition-colors line-clamp-2">
                  {article.title}
                </h3>
                <p className="text-xs md:text-sm text-foreground-600 leading-relaxed line-clamp-2 mb-3">
                  {article.description}
                </p>
                <div className="flex items-center justify-between text-xs text-foreground-500">
                  <span>{article.date}</span>
                  <span>{article.readTime}</span>
                </div>
              </div>
            </Link>
          ))}
        </div>
      </div>
    </section>
  );
}

function SafetySection() {
  return (
    <section className="py-14 md:py-20 px-4 md:px-6 bg-background-50">
      <div className="max-w-4xl mx-auto">
        <h2 className="font-heading text-2xl md:text-4xl font-bold text-foreground-950 mb-3 text-center">
          Is Itaobuy Sheet Safe?
        </h2>
        <p className="text-sm md:text-base text-foreground-600 text-center mb-10 max-w-xl mx-auto">
          Yes — but only when using verified sources. Here is what we evaluate to ensure you stay safe with every itaobuy spreadsheet interaction.
        </p>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 md:gap-6">
          <div className="p-5 md:p-6 bg-primary-50/50 rounded-xl border border-primary-200/50">
            <h3 className="flex items-center gap-2 font-semibold text-sm md:text-base text-primary-800 mb-3">
              <span className="w-7 h-7 flex items-center justify-center rounded-full bg-primary-100 text-primary-600">
                <i className="ri-check-line"></i>
              </span>
              Safe Usage
            </h3>
            <ul className="space-y-2.5 text-xs md:text-sm text-foreground-700">
              <li className="flex items-start gap-2">
                <span className="w-4 h-4 flex-shrink-0 flex items-center justify-center text-primary-500 mt-0.5">
                  <i className="ri-checkbox-circle-fill text-sm"></i>
                </span>
                Verified itaobuy spreadsheets with transparent data sources
              </li>
              <li className="flex items-start gap-2">
                <span className="w-4 h-4 flex-shrink-0 flex items-center justify-center text-primary-500 mt-0.5">
                  <i className="ri-checkbox-circle-fill text-sm"></i>
                </span>
                Official listings from trusted itaobuy sheet providers
              </li>
              <li className="flex items-start gap-2">
                <span className="w-4 h-4 flex-shrink-0 flex items-center justify-center text-primary-500 mt-0.5">
                  <i className="ri-checkbox-circle-fill text-sm"></i>
                </span>
                Structured data with clear update timestamps
              </li>
              <li className="flex items-start gap-2">
                <span className="w-4 h-4 flex-shrink-0 flex items-center justify-center text-primary-500 mt-0.5">
                  <i className="ri-checkbox-circle-fill text-sm"></i>
                </span>
                Regular security audits and verification checks
              </li>
            </ul>
          </div>

          <div className="p-5 md:p-6 bg-accent-50/50 rounded-xl border border-accent-200/50">
            <h3 className="flex items-center gap-2 font-semibold text-sm md:text-base text-accent-800 mb-3">
              <span className="w-7 h-7 flex items-center justify-center rounded-full bg-accent-100 text-accent-600">
                <i className="ri-close-line"></i>
              </span>
              Risky Usage
            </h3>
            <ul className="space-y-2.5 text-xs md:text-sm text-foreground-700">
              <li className="flex items-start gap-2">
                <span className="w-4 h-4 flex-shrink-0 flex items-center justify-center text-accent-500 mt-0.5">
                  <i className="ri-close-circle-fill text-sm"></i>
                </span>
                Unknown itaobuy sheet sources without verification
              </li>
              <li className="flex items-start gap-2">
                <span className="w-4 h-4 flex-shrink-0 flex items-center justify-center text-accent-500 mt-0.5">
                  <i className="ri-close-circle-fill text-sm"></i>
                </span>
                Random links shared in unmoderated forums
              </li>
              <li className="flex items-start gap-2">
                <span className="w-4 h-4 flex-shrink-0 flex items-center justify-center text-accent-500 mt-0.5">
                  <i className="ri-close-circle-fill text-sm"></i>
                </span>
                Unorganized spreadsheets with mixed data quality
              </li>
              <li className="flex items-start gap-2">
                <span className="w-4 h-4 flex-shrink-0 flex items-center justify-center text-accent-500 mt-0.5">
                  <i className="ri-close-circle-fill text-sm"></i>
                </span>
                Outdated itaobuy spreadsheet versions with broken links
              </li>
            </ul>
          </div>
        </div>

        <div className="mt-8 p-5 bg-background-100 rounded-xl border border-background-200/70 text-center">
          <p className="text-sm text-foreground-700 mb-3">
            We evaluate data transparency, source reliability, update frequency, and security structure for every itaobuy sheet we review.
          </p>
          <Link
            to="/itaobuy-sheet-guide-2026"
            className="inline-flex items-center gap-1.5 text-sm font-semibold text-primary-600 hover:text-primary-700 transition-colors cursor-pointer"
          >
            Read our full safety analysis
            <span className="w-4 h-4 flex items-center justify-center">
              <i className="ri-arrow-right-line"></i>
            </span>
          </Link>
        </div>
      </div>
    </section>
  );
}

function FinalCTA() {
  return (
    <section className="relative py-16 md:py-24 px-4 md:px-6">
      {/* Background image */}
      <div className="absolute inset-0">
        <img
          src="https://readdy.ai/api/search-image?query=Warm%20cozy%20product%20arrangement%20flat%20lay%20photography%20viewed%20from%20above%20with%20laptop%20showing%20spreadsheet%20comparison%20dashboard%20surrounded%20by%20folded%20neutral%20beige%20cream%20and%20warm%20grey%20clothing%20pieces%20leather%20wallet%20minimal%20watch%20ceramic%20coffee%20mug%20and%20succulent%20plant%20on%20light%20linen%20fabric%20soft%20diffused%20daylight%20casting%20subtle%20shadows%20editorial%20lifestyle%20product%20photography%20harmonious%20warm%20neutral%20palette&width=1600&height=600&seq=cta-final-v2-2026&orientation=landscape"
          alt="Itaobuy sheet platform call to action"
          className="w-full h-full object-cover object-top"
        />
        <div className="absolute inset-0 bg-gradient-to-b from-black/40 via-black/35 to-black/50"></div>
      </div>

      <div className="relative z-10 max-w-3xl mx-auto text-center">
        <h2 className="font-heading text-2xl md:text-4xl font-bold text-white mb-3">
          Start Using Itaobuy Sheet the Smart Way
        </h2>
        <p className="text-sm md:text-base text-white/80 mb-8 max-w-lg mx-auto">
          Stop guessing. Start comparing. Access the most trusted itaobuy spreadsheet platform today and make smarter purchasing decisions.
        </p>
        <div className="flex flex-col sm:flex-row items-center justify-center gap-3">
          <a
            href="https://kaspreadsheet.com"
            target="_blank"
            rel="noopener nofollow"
            className="w-full sm:w-auto px-8 py-3.5 bg-accent-500 text-background-50 font-semibold text-base rounded-full hover:bg-accent-600 transition-all duration-200 transform hover:scale-105 whitespace-nowrap cursor-pointer"
          >
            Access Top Ranked Sheet
          </a>
          <Link
            to="/itaobuy-sheet-guide-2026"
            className="w-full sm:w-auto px-8 py-3.5 bg-white/20 backdrop-blur-sm text-white font-semibold text-base rounded-full border border-white/30 hover:bg-white/35 transition-all duration-200 whitespace-nowrap cursor-pointer"
          >
            View Full Guide
          </Link>
          <a
            href="https://kaspreadsheet.com"
            target="_blank"
            rel="noopener nofollow"
            className="w-full sm:w-auto px-8 py-3.5 bg-primary-500 text-background-50 font-semibold text-base rounded-full hover:bg-primary-600 transition-all duration-200 transform hover:scale-105 whitespace-nowrap cursor-pointer"
          >
            Go to Main Platform
          </a>
        </div>
      </div>
    </section>
  );
}